import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../utils/firebaseConfig';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  startAfter,
} from 'firebase/firestore';
import { format, differenceInSeconds } from 'date-fns';
import { useTheme } from '../contexts/ThemeContext';
import { FiX } from 'react-icons/fi';
import classNames from 'classnames';

const AssignmentHistory = ({ traineeId, onClose }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [trainerNames, setTrainerNames] = useState({});
  const { isDarkMode } = useTheme();
  const modalRef = useRef(null);

  const fetchTrainerNames = useCallback(async () => {
    // Fetch all trainers and admins
    const trainerAdminQuery = query(
      collection(db, 'users'),
      where('role', 'in', ['trainer', 'admin'])
    );
    const trainerSnapshot = await getDocs(trainerAdminQuery);
    const names = {};
    trainerSnapshot.forEach((doc) => {
      names[doc.id] = doc.data().name;
    });
    setTrainerNames(names);
  }, []);

  const fetchAssignments = useCallback(
    async (lastDoc = null) => {
      try {
        let q = query(
          collection(db, 'assignmentHistory'),
          where('traineeId', '==', traineeId),
          orderBy('startDate', 'desc'),
          limit(10)
        );

        if (lastDoc) {
          q = query(q, startAfter(lastDoc));
        }

        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          setHasMore(false);
          return;
        }

        const assignmentData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            startDate: data.startDate ? data.startDate.toDate() : null,
            endDate: data.endDate ? data.endDate.toDate() : null,
          };
        });

        // Fetch trainer names if not already fetched
        if (Object.keys(trainerNames).length === 0) {
          await fetchTrainerNames();
        }

        // Calculate durations
        const now = new Date();
        for (let i = 0; i < assignmentData.length; i++) {
          const current = assignmentData[i];

          const startDate = current.startDate;
          const endDate = current.endDate || now;

          if (startDate && endDate && startDate <= endDate) {
            const totalSeconds = differenceInSeconds(endDate, startDate);

            const days = Math.floor(totalSeconds / (24 * 3600));
            const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;

            current.duration = { days, hours, minutes, seconds };
          } else {
            current.duration = null;
          }
        }

        // No need to reverse since we're already getting data in descending order
        setAssignments((prev) => [...prev, ...assignmentData]);
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      } catch (error) {
        console.error('Error fetching assignment history:', error);
      } finally {
        setLoading(false);
      }
    },
    [traineeId, fetchTrainerNames, trainerNames]
  );

  useEffect(() => {
    setAssignments([]);
    setLastVisible(null);
    setHasMore(true);
    // Initial focus on the modal container when traineeId changes and ref is available
    if (modalRef.current) {
      modalRef.current.focus(); 
    }
    fetchAssignments();
  }, [traineeId, fetchAssignments]);

  // New useEffect to scroll into view when loading is complete
  useEffect(() => {
    if (!loading && modalRef.current) {
      requestAnimationFrame(() => {
        if (modalRef.current) { // Check again as it might unmount
          // Optional: Re-focus if needed, though initial focus might be enough
          // modalRef.current.focus(); 
          modalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }
  }, [loading]); // Trigger when loading state changes

  const handleLoadMore = () => {
    if (lastVisible) {
      fetchAssignments(lastVisible);
    }
  };

  // Handle escape key press
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (loading && assignments.length === 0) {
    return (
      <div className="text-xs sm:text-sm text-text-light dark:text-text-dark transition-colors duration-300 p-2">
        Loading assignment history...
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/5 backdrop-blur-[2px] overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        ref={modalRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className="relative w-full max-w-4xl bg-primary-light dark:bg-primary-dark text-text-light dark:text-text-dark p-3 sm:p-6 lg:p-8 sm:rounded-lg shadow-xl transition-colors duration-300 mt-0 sm:mt-8 mx-auto"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-3 top-3 sm:right-4 sm:top-4 text-text-light/60 dark:text-text-dark/60 hover:text-text-light dark:hover:text-text-dark transition-colors duration-300"
          aria-label="Close modal"
        >
          <FiX className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>

        <div className="sm:text-center mb-6">
          <h2 className="text-base sm:text-xl lg:text-2xl font-bold mb-2 pr-8">Assignment History Report</h2>
          <p className="text-xs sm:text-sm text-text-light/70 dark:text-text-dark/70">
            Detailed record of FTO assignments and durations
          </p>
        </div>
        
        <div className="overflow-y-auto max-h-[calc(100vh-8rem)]">
          {assignments.length === 0 ? (
            <p className="text-center text-xs sm:text-sm text-text-light/70 dark:text-text-dark/70 py-4">
              No assignment history found.
            </p>
          ) : (
            <>
              {/* Mobile View (< 640px) */}
              <div className="block sm:hidden space-y-2">
                {assignments.map((assignment, index) => (
                  <div
                    key={`${assignment.id}-${index}`}
                    className="bg-secondary-light/50 dark:bg-secondary-dark/50 p-2.5 rounded-lg shadow-sm transition-colors duration-300 border border-secondary-light/10 dark:border-secondary-dark/10"
                  >
                    <p className="font-semibold text-xs mb-1.5">
                      {trainerNames[assignment.trainerId] || 'Unknown'} (FTO)
                    </p>
                    <div className="space-y-1 text-[11px] leading-relaxed">
                      <p className="flex items-center justify-between">
                        <span className="text-text-light/70 dark:text-text-dark/70">Start:</span>
                        <span className="font-medium">
                          {assignment.startDate
                            ? format(assignment.startDate, 'MMM d, yyyy h:mm a')
                            : 'Unknown'}
                        </span>
                      </p>
                      <p className="flex items-center justify-between">
                        <span className="text-text-light/70 dark:text-text-dark/70">End:</span>
                        <span className="font-medium">
                          {assignment.endDate
                            ? format(assignment.endDate, 'MMM d, yyyy h:mm a')
                            : 'Current'}
                        </span>
                      </p>
                      {assignment.duration && (
                        <p className="flex items-center justify-between border-t border-secondary-light/10 dark:border-secondary-dark/10 pt-1 mt-1">
                          <span className="text-text-light/70 dark:text-text-dark/70">Duration:</span>
                          <span className="font-medium">
                            {assignment.duration.days}d {assignment.duration.hours}h {assignment.duration.minutes}m
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Tablet and Desktop View (≥ 640px) */}
              <div className="hidden sm:block">
                <div className="bg-secondary-light/30 dark:bg-secondary-dark/30 rounded-lg p-4 lg:p-6 border border-secondary-light/10 dark:border-secondary-dark/10">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-secondary-light/20 dark:border-secondary-dark/20">
                        <th className="text-left py-2 px-3 text-xs lg:text-sm font-semibold">FTO Name</th>
                        <th className="text-left py-2 px-3 text-xs lg:text-sm font-semibold">Start Date</th>
                        <th className="text-left py-2 px-3 text-xs lg:text-sm font-semibold">End Date</th>
                        <th className="text-left py-2 px-3 text-xs lg:text-sm font-semibold">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignments.map((assignment, index) => (
                        <tr 
                          key={`${assignment.id}-${index}`}
                          className={classNames(
                            'transition-colors duration-300',
                            index % 2 === 0 
                              ? 'bg-secondary-light/10 dark:bg-secondary-dark/10' 
                              : 'bg-transparent'
                          )}
                        >
                          <td className="py-2.5 px-3 text-xs lg:text-sm">
                            {trainerNames[assignment.trainerId] || 'Unknown'}
                          </td>
                          <td className="py-2.5 px-3 text-xs lg:text-sm">
                            {assignment.startDate
                              ? format(assignment.startDate, 'MMM d, yyyy h:mm a')
                              : 'Unknown'}
                          </td>
                          <td className="py-2.5 px-3 text-xs lg:text-sm">
                            {assignment.endDate
                              ? format(assignment.endDate, 'MMM d, yyyy h:mm a')
                              : 'Current'}
                          </td>
                          <td className="py-2.5 px-3 text-xs lg:text-sm font-medium">
                            {assignment.duration
                              ? `${assignment.duration.days}d ${assignment.duration.hours}h ${assignment.duration.minutes}m`
                              : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {hasMore && (
            <div className="mt-4 text-center">
              <button
                onClick={handleLoadMore}
                className="px-4 py-2 text-xs sm:text-sm bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark rounded-md hover:brightness-110 transition-all duration-300"
              >
                Load More
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignmentHistory;